import React, { useCallback, useEffect, useState } from 'react';
import { KeyRound, Plus, Trash2, Copy } from 'lucide-react';
import { apiKeysAPI } from '../services/api';
import useConfirm from '../hooks/useConfirm';
import toast from '../utils/toast';

const errorText = (err) => err?.response?.data?.error || err?.message || 'Something went wrong';
const dateText = (iso) => (iso ? new Date(iso).toLocaleDateString() : 'never');

const ApiKeysCard = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [keys, setKeys] = useState(null); // null = loading
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [cap, setCap] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newKey, setNewKey] = useState(null); // the full key, shown once

  const load = useCallback(async () => {
    try {
      const res = await apiKeysAPI.list();
      setKeys(res.data.keys || []);
    } catch (err) {
      setKeys([]);
      setError(errorText(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = { name: name.trim() };
      if (cap.trim()) body.max_results_per_search = Number(cap);
      const res = await apiKeysAPI.create(body);
      setNewKey(res.data.key);
      setCreating(false);
      setName('');
      setCap('');
      await load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(newKey);
      toast.success('Key copied');
    } catch (err) {
      toast.error('Could not copy. Select the key and copy it by hand.');
    }
  };

  const revoke = async (k) => {
    const ok = await confirm({
      title: 'Revoke this key?',
      message: `"${k.name}" stops working straight away. Anything using it will need a new key.`,
      confirmText: 'Revoke key',
    });
    if (!ok) return;
    try {
      await apiKeysAPI.revoke(k.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <div id="api-keys" className="settings-card">
      <div className="settings-card-head">
        <span className="settings-card-icon"><KeyRound size={17} /></span>
        <div className="settings-card-titles">
          <h3>API keys</h3>
          <p>Let a tool such as Claude or Command Center start searches and read your prospects and research results for you. A key cannot send messages, change campaigns, delete anything or touch settings or billing.</p>
        </div>
      </div>

      {newKey && (
        <div className="research-profile-form" role="status">
          <label htmlFor="new-api-key">Your new key. Copy it now, it is shown only once.</label>
          <input id="new-api-key" type="text" readOnly value={newKey} onFocus={(e) => e.target.select()} />
          <div className="settings-card-foot">
            <button type="button" className="test-button" onClick={() => setNewKey(null)}>Done</button>
            <button type="button" className="card-save-button" onClick={copy}><Copy size={14} /> Copy key</button>
          </div>
        </div>
      )}

      {keys === null && <p className="setting-note">Loading...</p>}

      {!creating && keys !== null && (
        <>
          {keys.length === 0 && <p className="setting-note">No keys yet.</p>}
          {keys.length > 0 && (
            <ul className="research-profile-list">
              {keys.map((k) => (
                <li key={k.id} className="research-profile-item">
                  <div className="research-profile-main">
                    <strong>{k.name}</strong>
                    <div className="research-profile-meta">
                      {k.key_prefix}… · last used {dateText(k.last_used_at)}
                      {k.max_results_per_search ? ` · max ${k.max_results_per_search} results per search` : ' · no limit per search'}
                    </div>
                  </div>
                  <div className="research-profile-actions">
                    <button type="button" className="research-profile-btn research-profile-btn-danger" onClick={() => revoke(k)} aria-label={`Revoke ${k.name}`}>
                      <Trash2 size={14} /> Revoke
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {error && <small className="setting-error" role="alert">{error}</small>}
          <div className="settings-card-foot">
            <button type="button" className="card-save-button" onClick={() => { setCreating(true); setError(''); setNewKey(null); }}>
              <Plus size={14} /> New key
            </button>
          </div>
        </>
      )}

      {creating && (
        <form onSubmit={create} className="research-profile-form">
          <label htmlFor="ak-name">Name</label>
          <input id="ak-name" type="text" maxLength={80} required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="e.g. Claude" />
          <label htmlFor="ak-cap">Most results per search (optional)</label>
          <input id="ak-cap" type="number" min="1" step="1" value={cap}
            onChange={(e) => setCap(e.target.value)} placeholder="No limit" />
          <small className="setting-help">
            Searches use your credits, one for each prospect that comes back with an email or phone number. Leave this empty for no limit, or set a number to cap what this key can spend on one search.
          </small>
          {error && <small className="setting-error" role="alert">{error}</small>}
          <div className="settings-card-foot">
            <button type="button" className="test-button" onClick={() => { setCreating(false); setError(''); }} disabled={saving}>Cancel</button>
            <button type="submit" className="card-save-button" disabled={saving || !name.trim()}>
              {saving ? 'Creating...' : 'Create key'}
            </button>
          </div>
        </form>
      )}
      {confirmDialog}
    </div>
  );
};

export default ApiKeysCard;
